'use client'

import { Suspense, useEffect, useRef, useState, useTransition } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import styles from './currency-chooser.module.css'
import { FaChevronDown } from 'react-icons/fa'

const OPTIONS = [
    { code: 'eur', label: 'EUR', symbol: '€' },
    { code: 'usd', label: 'USD', symbol: '$' },
    { code: 'gel', label: 'GEL', symbol: '₾' },
]

// Case-insensitive on purpose: the server prices `?currency=GEL` in lari, so a
// strict match here left the header saying "€ EUR" above lari amounts.
const byCode = (code) => OPTIONS.find((o) => o.code === String(code ?? '').trim().toLowerCase())

function Placeholder() {
    return (
        <div
            className={styles.dropdown}
            style={{ minWidth: 60, minHeight: 38, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
            <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    )
}

function CurrencyChooserInner() {
    const router = useRouter()
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const [open, setOpen] = useState(false)
    const [pending, startTransition] = useTransition()
    const rootRef = useRef()

    // The URL is the source of truth (the server prices in this currency).
    // localStorage only seeds the first visit, and it can't be read during SSR,
    // so it lands after mount — hence the placeholder rather than a guess that
    // would hydrate-mismatch.
    const [stored, setStored] = useState(null)
    const [mounted, setMounted] = useState(false)
    useEffect(() => {
        try {
            const rec = JSON.parse(localStorage.getItem('recordings') || '{}')
            setStored(byCode(rec.currency) ?? null)
        } catch { }
        setMounted(true)
    }, [])

    useEffect(() => {
        function onClick(e) {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener('mousedown', onClick)
        return () => document.removeEventListener('mousedown', onClick)
    }, [])

    // Derived, not stored: reading straight off the URL means a soft navigation
    // re-renders this with the new currency — no reload, no state to resync.
    const currency = byCode(searchParams.get('currency')) ?? (mounted ? stored ?? OPTIONS[0] : null)

    const choose = (opt) => {
        setOpen(false)
        if (opt.code === currency?.code) return
        try {
            const rec = JSON.parse(localStorage.getItem('recordings') || '{}')
            rec.currency = opt.code
            localStorage.setItem('recordings', JSON.stringify(rec))
            window.dispatchEvent(new Event('storageUpdate'))
        } catch { }

        const params = new URLSearchParams(searchParams.toString())
        params.set('currency', opt.code)
        // replace (not push) so the back button doesn't walk through every
        // currency the visitor tried; scroll:false keeps their place in the list
        startTransition(() => {
            router.replace(`${pathname}?${params.toString()}`, { scroll: false })
        })
    }

    if (!currency) return <Placeholder />

    return (
        <div className={styles.dropdown} ref={rootRef} data-pending={pending ? '' : undefined}>
            <button
                className={styles.toggle}
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-busy={pending}
            >
                <span className={styles.symbol}>{currency.symbol}</span>
                <span className={styles.code}>{currency.label}</span>
                <FaChevronDown className={`${styles.arrow} ${open ? styles.open : ''}`} />
            </button>

            {open && (
                <ul className={styles.options} role="listbox">
                    {OPTIONS.map((opt) => (
                        <li
                            key={opt.code}
                            role="option"
                            aria-selected={opt.code === currency.code}
                            className={`
                                ${styles.option}
                                ${opt.code === currency.code ? styles.selected : ''}
                            `}
                            onClick={() => choose(opt)}
                        >
                            <span className={styles.symbol}>{opt.symbol}</span>
                            <span className={styles.code}>{opt.label}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

export default function CurrencyChooser() {
    // useSearchParams needs a Suspense boundary or it opts every statically
    // rendered page that shows the nav (/terms, /message/*) into client render.
    return (
        <Suspense fallback={<Placeholder />}>
            <CurrencyChooserInner />
        </Suspense>
    )
}
